/**
 * As caixas de entrada que este agente atende (spec 157, FR-006).
 *
 * **O QUE MUDOU.** Até a spec 096 a chave apontava UM agente para a organização
 * inteira (um ponteiro nos settings da organização). Agora o que aponta para o agente é a
 * CAIXA DE ENTRADA da conta do canal: cada caixa tem um agente, e "qualquer caixa"
 * é o vínculo que vale para as que não têm o seu.
 *
 * **UMA CAIXA, UM AGENTE.** Caixa já ligada a outro atendimento aparece com o nome
 * dele e um botão para trazê-la para cá, porque o servidor recusa (409) ligar por
 * cima: trocar é desligar lá e ligar aqui, e a tela faz os dois passos.
 *
 * **A TROCA VALE DA PRÓXIMA CONVERSA EM DIANTE.** As conversas abertas terminam
 * com quem começou. Desligar não apaga histórico.
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Inbox, Loader2, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { Switch } from '@/components/ui/switch'
import { atendimentoService } from '@/services/painel'
import type { AgenteDeAtendimento, CaixasDaConta } from '@/schemas/painel'

interface Props {
  organizationId?: string | null
  agentId?: string
  atendimento?: AgenteDeAtendimento | null
  aoMudar: () => void | Promise<void>
}

type Linha = {
  chave: string
  inboxId: number | null
  nome: string
  canal?: string | null
  linkId: string | null
  agentId: string | null
  agentName: string | null
}

const QUALQUER = 'qualquer-caixa'

function linhasDe(caixas: CaixasDaConta): Linha[] {
  const qualquer: Linha = {
    chave: QUALQUER,
    inboxId: null,
    nome: 'Qualquer caixa da conta',
    linkId: caixas.any_inbox?.link_id ?? null,
    agentId: caixas.any_inbox?.agent_id ?? null,
    agentName: caixas.any_inbox?.agent_name ?? null,
  }
  return [
    qualquer,
    ...caixas.inboxes.map((c) => ({
      chave: String(c.inbox_id),
      inboxId: c.inbox_id,
      nome: c.name,
      canal: c.channel_type ?? null,
      linkId: c.link_id,
      agentId: c.agent_id,
      agentName: c.agent_name,
    })),
  ]
}

export function ChaveDeAtendimento({
  organizationId,
  agentId,
  atendimento,
  aoMudar,
}: Props) {
  const [linhas, setLinhas] = useState<Linha[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gravando, setGravando] = useState<string | null>(null)
  // AGENTE QUE AINDA NÃO EXISTE NÃO PODE SER LIGADO: o vínculo guarda um
  // identificador, e ele só nasce no primeiro salvamento.
  const disponivel = !!organizationId && !!agentId

  const carregar = useCallback(async () => {
    if (!organizationId) return
    setCarregando(true)
    setErro(null)
    try {
      setLinhas(linhasDe(await atendimentoService.caixas(organizationId)))
    } catch {
      setLinhas(null)
      setErro(
        'Não foi possível ler as caixas do canal agora. Nada foi alterado; tente de novo em instantes.'
      )
    } finally {
      setCarregando(false)
    }
  }, [organizationId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const alternar = async (linha: Linha, trazerParaCa = false) => {
    if (!organizationId || !agentId) return
    setGravando(linha.chave)
    try {
      const minha = linha.agentId === agentId
      if (minha && linha.linkId) {
        await atendimentoService.desligarCaixa(organizationId, linha.linkId)
        toast.success(
          `Este agente não atende mais ${linha.inboxId === null ? 'as outras caixas' : `a caixa ${linha.nome}`}.`
        )
      } else {
        if (trazerParaCa && linha.linkId) {
          await atendimentoService.desligarCaixa(organizationId, linha.linkId)
        }
        await atendimentoService.ligarCaixa(organizationId, linha.inboxId, agentId)
        toast.success(
          `Este agente passa a atender ${linha.inboxId === null ? 'qualquer caixa da conta' : `a caixa ${linha.nome}`} nas próximas conversas.`
        )
      }
      await carregar()
      await aoMudar()
    } catch {
      toast.error('Não foi possível gravar o vínculo da caixa.')
    } finally {
      setGravando(null)
    }
  }

  const atendeAlguma = !!agentId && !!linhas?.some((l) => l.agentId === agentId)

  return (
    <div className="space-y-3 rounded-xl border border-gray-700/50 bg-white/5 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-medium text-gray-200">Caixas de entrada que este agente atende</p>
          <p className="mt-1 text-sm text-gray-400">
            Cada caixa da conta do canal é atendida por um agente. Ligar uma caixa
            aqui libera o passo <strong>Atendimento</strong>, onde o comportamento é
            descrito.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void carregar()}
          disabled={carregando || !organizationId}
          className="rounded-md p-2 text-gray-400 hover:text-gray-200 disabled:opacity-50"
          aria-label="Reler as caixas do canal"
        >
          {carregando ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </button>
      </div>

      {!disponivel && (
        <p className="text-xs text-gray-500">
          Disponível depois de salvar o agente pela primeira vez.
        </p>
      )}

      {erro && (
        <p role="alert" className="text-sm text-amber-200/90">
          {erro}
        </p>
      )}

      {linhas && (
        <ul className="divide-y divide-gray-700/50 rounded-lg border border-gray-700/50">
          {linhas.map((linha) => {
            const minha = !!agentId && linha.agentId === agentId
            const deOutro = !!linha.agentId && !minha
            return (
              <li
                key={linha.chave}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Inbox className="h-4 w-4 flex-shrink-0 text-gray-500" />
                  <div className="min-w-0">
                    <p className="truncate text-sm text-gray-200">
                      {linha.nome}
                      {linha.canal && (
                        <span className="ml-2 text-xs text-gray-500">{linha.canal}</span>
                      )}
                    </p>
                    {deOutro && (
                      <p className="text-xs text-gray-400">
                        Atendida por <strong>{linha.agentName ?? 'outro agente'}</strong>.
                        Trazer para cá desliga lá; vale a partir da próxima conversa.
                      </p>
                    )}
                  </div>
                </div>
                {deOutro ? (
                  <button
                    type="button"
                    onClick={() => void alternar(linha, true)}
                    disabled={!disponivel || gravando !== null}
                    className="whitespace-nowrap rounded-md border border-gray-600 px-2 py-1 text-xs text-gray-200 hover:bg-white/10 disabled:opacity-50"
                  >
                    Trazer para este agente
                  </button>
                ) : (
                  <Switch
                    aria-label={`${linha.nome}: atendida por este agente`}
                    checked={minha}
                    disabled={!disponivel || gravando !== null}
                    onCheckedChange={() => void alternar(linha)}
                  />
                )}
              </li>
            )
          })}
        </ul>
      )}

      {gravando && (
        <p className="flex items-center gap-2 text-xs text-gray-500">
          <Loader2 className="h-3 w-3 animate-spin" /> gravando…
        </p>
      )}

      <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" />
        <div className="text-xs text-amber-200/90">
          <p>
            <strong>Isto faz o agente responder gente de verdade.</strong>
          </p>
          {atendimento?.blueprint_version ? (
            <p className="mt-1">
              Está no ar o documento <strong>v{atendimento.blueprint_version}</strong>
              {atendimento.blueprint_published_at &&
                `, publicado em ${new Date(
                  atendimento.blueprint_published_at
                ).toLocaleDateString('pt-BR')}`}
              . O rascunho que você estiver editando não vale até ser publicado.
            </p>
          ) : (
            <p className="mt-1">
              Ainda não há documento publicado para este atendimento: sem ele o
              agente atende sem documento que descreva o comportamento.
            </p>
          )}
          {atendeAlguma && (
            <p className="mt-1">
              Desligar não apaga o histórico: as conversas que este agente atendeu
              continuam atribuídas a ele.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
